import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${API_BASE_URL}/api/v1/route-auth-rules`;

export default function useRouteScopeRules(page, pageSize) {
  const authToken = sessionStorage.getItem("authToken");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      const status = err.response.status;

      if (status >= 500 && status < 600) {
        message = "Something went wrong. Please try again later.";
      } else if (status === 400 || status === 401) {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch route auth rules
  const fetchAllRouteRules = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);

    try {
      const res = await apiClient.get(
        `${BASE_URL}?page=${page}&size=${pageSize}`
      );

      setRoutes(res.data.content);
      setTotalPages(res.data.totalPages || 1);
      setTotalElements(res.data.totalElements || 0);
    } catch (error) {
      handleAxiosErrors(error, "fetch all route rules");
    } finally {
      setLoading(false);
    }
  }, [authToken, page, pageSize]);

  // fetch role by id
  const fetchRouteRuleById = useCallback(
    async (ruleId) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const res = await apiClient.get(`${BASE_URL}/${ruleId}`);

        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch route rule by id");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // add rule
  const addRouteRule = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(BASE_URL, payload);

        setRoutes((prev) => [res.data, ...prev]);
        toast.success("Route rule created successfully");
      } catch (error) {
        handleAxiosErrors(error, "add route rule");
      }
    },
    [authToken]
  );

  // bulk import routes
  const bulkImportRoutes = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(`${BASE_URL}/bulk`, payload);

        setRoutes((prev) => [...res.data, ...prev]);
        toast.success("Bulk routes added successfully");
      } catch (error) {
        handleAxiosErrors(error, "add route rule");
      }
    },
    [authToken]
  );

  // update rule
  const updateRouteRule = useCallback(
    async (payload, ruleId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(`${BASE_URL}/${ruleId}`, payload);

        setRoutes((prev) =>
          prev.map((rule) => (rule.id === ruleId ? res.data : rule))
        );
        toast.success("Rule updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update route rule");
      }
    },
    [authToken]
  );

  // patch rule
  const patchRouteRule = useCallback(
    async (patches, ruleId) => {
      if (!authToken || patches.length === 0) return;

      console.log("patches =>", patches);
      try {
        const res = await apiClient.patch(`${BASE_URL}/${ruleId}`, patches);

        setRoutes((prev) => prev.map((r) => (r.id === ruleId ? res.data : r)));

        toast.success("Route updated successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "update route rule");
        throw error;
      }
    },
    [authToken]
  );

  // delete rule
  const deleteRouteRuleById = useCallback(
    async (ruleId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_URL}/${ruleId}`);

        setRoutes((prev) => prev.filter((rule) => rule.id !== ruleId));
        toast.success("Route rule deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete rule");
      }
    },
    [authToken]
  );

  // search route
  const searchRouteRules = useCallback(
    async (query = "", searchPage = page, searchSize = pageSize) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const trimmedQuery = query?.trim() || "";
        const hasSearch = trimmedQuery.length >= 3;
        const params = new URLSearchParams({
          page: String(searchPage),
          size: String(searchSize),
        });
        if (hasSearch) {
          params.append("q", trimmedQuery);
        }
        const endpoint = hasSearch ? `${BASE_URL}/search` : BASE_URL;
        const url = `${endpoint}?${params.toString()}`;
        const res = await apiClient.get(url);
        const items = Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data)
          ? res.data
          : [];
        setRoutes(items);
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || items.length || 0);
      } catch (error) {
        handleAxiosErrors(error, "search route rules");
      } finally {
        setLoading(false);
      }
    },
    [authToken, page, pageSize]
  );

  return {
    routes,
    loading,
    fetchRouteRuleById,
    addRouteRule,
    bulkImportRoutes,
    updateRouteRule,
    patchRouteRule,
    deleteRouteRuleById,
    fetchAllRouteRules,
    searchRouteRules,
    totalPages,
    totalElements,
  };
}
